import { useWallet } from "@/hooks/useWallet";
import { usePerpsPositions } from "@/hooks/usePerpsPositions";
import { useLiveSnapshot } from "@/hooks/useLiveSnapshot";
import { StatCard } from "@/components/dashboard/StatCard";
import { PerpsPositionsTable } from "@/components/perps/PerpsPositionsTable";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";

export default function PerpsPositionsRisk() {
  const { address } = useWallet();
  const { data, isLoading, error, refresh } = usePerpsPositions(address);
  const { data: liveSnapshot } = useLiveSnapshot(address, true);

  const state = (liveSnapshot?.perps.state as any) ?? data?.state;
  const positions: any[] = Array.isArray(state?.assetPositions) ? state.assetPositions : [];
  const marginSummary = state?.marginSummary ?? state?.crossMarginSummary ?? null;

  const accountValue = liveSnapshot?.perps.accountValue ?? Number(marginSummary?.accountValue ?? marginSummary?.accountValueUsd ?? 0);
  const totalMarginUsed = liveSnapshot?.perps.marginUsed ?? Number(marginSummary?.totalMarginUsed ?? marginSummary?.marginUsed ?? 0);
  const unrealizedFromSummary = Number(marginSummary?.totalUnrealizedPnl ?? marginSummary?.unrealizedPnl ?? NaN);
  const unrealizedFromPositions = positions.reduce((sum, p) => {
    const pos = p?.position ?? p;
    const pnl = Number(pos?.unrealizedPnl ?? pos?.unrealizedPnlUsd ?? pos?.pnl ?? 0);
    return sum + (Number.isFinite(pnl) ? pnl : 0);
  }, 0);
  const totalUnrealizedPnl = liveSnapshot?.perps.unrealizedPnl ?? (Number.isFinite(unrealizedFromSummary) ? unrealizedFromSummary : unrealizedFromPositions);

  return (
    <main className="container px-4 py-8 space-y-6">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-lg font-semibold">Positions & Risk</h2>
        <button
          className="text-sm text-muted-foreground hover:text-foreground"
          onClick={refresh}
          disabled={isLoading}
        >
          Refresh
        </button>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard
          title="Account Value"
          value={accountValue ? `$${accountValue.toFixed(2)}` : "N/A"}
          loading={isLoading}
          tooltip="Current perps margin account value reported by Hyperliquid for this wallet."
        />
        <StatCard
          title="Margin Used"
          value={totalMarginUsed ? `$${totalMarginUsed.toFixed(2)}` : "N/A"}
          loading={isLoading}
          tooltip="Total margin currently used by open perps positions and orders."
        />
        <StatCard
          title="Unrealized PnL"
          value={Number.isFinite(totalUnrealizedPnl) ? `${totalUnrealizedPnl >= 0 ? "+" : ""}$${totalUnrealizedPnl.toFixed(2)}` : "N/A"}
          loading={isLoading}
          tooltip="PnL on open perps positions that has not been realized yet. Updates with mark price changes."
        />
      </div>

      {positions.length > 0 ? (
        <PerpsPositionsTable positions={positions} />
      ) : (
        <div className="text-sm text-muted-foreground">
          No open perps positions found.
        </div>
      )}

      {!positions.length && state && (
        <div className="text-xs text-muted-foreground">
          If this looks wrong, ensure this wallet is the onchain address used by Hyperliquid (not an agent address).
        </div>
      )}
    </main>
  );
}
